import { AxiosError } from 'axios';
import { GetUserProfileByUsernameResult, User } from 'common/interfaces/api';
import { parseJSON, stringifyJSON } from 'common/utils';
import { generateCookieList } from 'common/utils/trackingTag';
import { generateUserStructuredData } from 'common/utils/user';
import Icon from 'components/common/Icon';
import ProfilePageContent from 'components/v2/modules/profile-page';
import { SITE_BASE_URL } from 'config/env';
import { parse } from 'cookie';
import { NextPageContext } from 'next';
import Head from 'next/head';
import { useEffect, useMemo, useState } from 'react';
import { DehydratedState, QueryClient, useQuery } from 'react-query';
import { dehydrate, Hydrate } from 'react-query/hydration';
import { getProfileTimelinePosts, getUserProfile } from 'service/user';
import useGlobalStore from 'store/global';
import useStore from 'store/timeline';
import styles from './ProfilePage.module.scss';
type ProfilePageProps = {
  username: string;
  userProfile: string | null;
  dehydratedStateStr?: string;
};

const ProfilePage = (props: ProfilePageProps) => {
  const username = props.username;
  const dehydratedState = useMemo(
    () =>
      props.dehydratedStateStr
        ? (parseJSON(props.dehydratedStateStr) as DehydratedState)
        : undefined,
    [props.dehydratedStateStr],
  );
  const [userProfile, setUserProfile] = useState(
    parseJSON(props.userProfile) as User,
  );
  const currentAuth = useGlobalStore((state) => state.currentAuth);
  const currentUser = useStore((state) => state.currentUser);
  const isCurrentUser = username === currentUser?.username;
  const isAuthInit = useGlobalStore((state) => state.isAuthInit);
  const shouldRefetchCurrentProfile = useStore(
    (state) => state.shouldRefetchCurrentProfile,
  );
  const shouldRefetchProfile = useStore((state) => state.shouldRefetchProfile);

  useEffect(() => {
    if (isCurrentUser) return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.id]);
  // profile
  const { refetch, isError } = useQuery(
    ['getProfile', username],
    async () => {
      if (!username) return Promise.reject();
      return getUserProfile(username);
    },
    {
      enabled: isAuthInit && !!username,
      refetchOnWindowFocus: false,
      retry: (count, e) => {
        const error = e as AxiosError;
        if (error.response?.status === 404) {
          return false;
        }
        if (count >= 2) {
          return false;
        }
        return true;
      },
      onSuccess: (data) => {
        const profile = data?.data;
        setUserProfile(profile);
      },
    },
  );

  useEffect(() => {
    if (shouldRefetchCurrentProfile) {
      void refetch();
    }
  }, [shouldRefetchCurrentProfile, refetch]);

  useEffect(() => {
    if (shouldRefetchProfile && isCurrentUser) {
      void refetch();
    }
  }, [shouldRefetchProfile, refetch, isCurrentUser]);

  useEffect(() => {
    if (currentAuth?.uid && isAuthInit) {
      void refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAuth?.uid]);

  // Metadata
  const titleText =
    (userProfile?.name ? userProfile.name : '') +
    (userProfile?.username ? ` (@${userProfile.username})` : '') +
    (userProfile ? ' | ' : '') +
    'PostPrime | 投資を楽しく学べる！';

  const canonicalUrl =
    userProfile?.username === 'PostPrime'
      ? 'https://postprime.com'
      : `${SITE_BASE_URL}/${username}`;

  return (
    <Hydrate state={dehydratedState}>
      <Head>
        {/* HTML Meta Tags */}
        <title>{titleText}</title>
        <meta
          name="description"
          content={`${
            userProfile?.bio
              ? userProfile?.bio
              : 'ログインして、SNSでお金を学ぼう!'
          }`}
        />

        {/* Facebook Meta Tags */}
        <meta property="og:url" content={`${SITE_BASE_URL}/${username}`} />
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={titleText} />
        <meta
          property="og:description"
          content={`${
            userProfile?.bio
              ? userProfile?.bio
              : 'ログインして、SNSでお金を学ぼう!'
          }`}
        />
        {(userProfile?.background_image || userProfile?.avatar_url) && (
          <meta
            property="og:image"
            key="og:image"
            content={
              userProfile?.background_image || userProfile?.avatar_url || ''
            }
          />
        )}

        {/* Twitter Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`${SITE_BASE_URL}/${username}`} />
        <meta name="twitter:title" content={titleText} />
        <meta
          name="twitter:description"
          content={`${
            userProfile?.bio
              ? userProfile?.bio
              : 'ログインして、SNSでお金を学ぼう!'
          }`}
        />
        {(userProfile?.background_image || userProfile?.avatar_url) && (
          <meta
            name="twitter:image"
            content={
              userProfile?.background_image || userProfile?.avatar_url || ''
            }
          />
        )}

        <link rel="canonical" href={canonicalUrl} />

        {/* Structured Data JSON-LD */}
        {userProfile && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: generateUserStructuredData(userProfile),
            }}
          />
        )}
      </Head>
      {isError ? (
        <div className={styles.emptyWrapper}>
          <Icon name="empty" width={96} height={96} />
          <div className={styles.emptyText}>このユーザーが存在しません</div>
        </div>
      ) : (
        userProfile && (
          <div className={styles.profilePageWrapper}>
            <ProfilePageContent
              username={username}
              userProfile={userProfile}
              enableTabs={
                !!userProfile?.is_creator &&
                !!userProfile?.is_open_for_subscription &&
                !!userProfile?.is_official_account
              }
            />
          </div>
        )
      )}
    </Hydrate>
  );
};

export const getServerSideProps = async (context: NextPageContext) => {
  const username = context.query.username as string;
  const referral = context.query.referral as string;
  const cookiesList = generateCookieList(
    context.query,
    parse(context.req?.headers?.cookie || ''),
  );
  if (cookiesList.length > 0) {
    context.res.setHeader('Set-Cookie', cookiesList);
  }

  let profileData: GetUserProfileByUsernameResult;
  const queryClient = new QueryClient();
  const requestConfig = context.req?.headers?.cookie
    ? {
        headers: {
          Cookie: context.req.headers.cookie,
        },
      }
    : undefined;

  try {
    profileData = await getUserProfile(username, true, referral);

    if (profileData?.data?.id) {
      try {
        await queryClient.prefetchInfiniteQuery(
          ['profile-posts', String(profileData.data.id)],
          ({ pageParam = '' }) =>
            getProfileTimelinePosts(
              profileData.data.id,
              String(pageParam),
              requestConfig,
            ),
          { staleTime: 15 * 60 * 1000 },
        );
      } catch (error) {
        // Ignore timeline prefetch errors so the profile page still SSRs.
      }
    }

    return {
      props: {
        username: username,
        userProfile: stringifyJSON(profileData?.data) as string,
        dehydratedStateStr: stringifyJSON(dehydrate(queryClient)) as string,
      },
    };
  } catch (error) {
    return {
      props: {
        username: username,
        userProfile: null,
      },
    };
  }
};
export default ProfilePage;
